import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ExpandMore } from "@mui/icons-material";
import HelpInput from "../../../../Inputs/HelpInput/HelpInput";
import {
  isPositiveConstraint,
  useConstraint,
} from "../../../../../hooks/useConstraint";
import { useMemo, useState } from "react";
import InputStack from "../../../../Styled/Stack/InputStack";

const coefficient = 11850000;

export default function CriticalRotationSpeed({ isDisabled }) {
  const { t } = useTranslation();

  const [innerRadius, setInnerRadius] = useState(0);
  const [outerRadius, setOuterRadius] = useState(0);
  const [length, setLength] = useState(0);
  const [rotationSpeed, setRotationSpeed] = useState(0);

  const handleInnerRadiusChange = useConstraint(
    setInnerRadius,
    isPositiveConstraint
  );

  const handleOuterRadiusChange = useConstraint(
    setOuterRadius,
    isPositiveConstraint
  );

  const handleRotationSpeedChange = useConstraint(
    setRotationSpeed,
    isPositiveConstraint
  );

  const handleLengthChange = useConstraint(setLength, isPositiveConstraint);

  const criticalRotationSpeed = useMemo(() => {
    const innerDiameter = innerRadius * 2;
    const outerDiameter = outerRadius * 2;
    return (
      (coefficient / length / length) *
      Math.sqrt(innerDiameter * innerDiameter + outerDiameter * outerDiameter)
    );
  }, [innerRadius, outerRadius, length]);

  const isRotationSpeedValid = useMemo(
    () => criticalRotationSpeed <= rotationSpeed * 1.4,
    [criticalRotationSpeed, rotationSpeed]
  );

  const isInvalid = useMemo(
    () => isNaN(criticalRotationSpeed) || isNaN(rotationSpeed),
    [criticalRotationSpeed, rotationSpeed]
  );

  const indicatorColor = useMemo(() => {
    if (isInvalid) return "black";
    return isRotationSpeedValid ? "green" : "red";
  }, [isRotationSpeedValid, isInvalid]);

  const criticalRotationSpeedText = useMemo(() => {
    if (isInvalid)
      return t(
        "pages.driveshaftTolerance.components.other.criticalRotationSpeed.components.results.options.uncertain"
      );
    return isRotationSpeedValid
      ? t(
          "pages.driveshaftTolerance.components.other.criticalRotationSpeed.components.results.options.possible"
        )
      : t(
          "pages.driveshaftTolerance.components.other.criticalRotationSpeed.components.results.options.notPossible"
        );
  }, [isRotationSpeedValid, isInvalid, t]);

  return (
    <Accordion sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h5">
          {t("pages.driveshaftTolerance.components.other.criticalRotationSpeed.title")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {/* Inputs for Frequency and Mass */}
        <Typography variant="h6" sx={{ my: 2 }}>
          {t("pages.driveshaftTolerance.components.other.criticalRotationSpeed.components.geometricCharacteristics.title")}
        </Typography>
        <InputStack>
          <HelpInput
            label={`R, ${t("units.length.mm")}`}
            type="number"
            variant="outlined"
            fullWidth
            value={outerRadius || ""} // Bind value to state
            onChange={handleOuterRadiusChange} // Use callback for handling input change
            helpButtonText={t(
              "pages.driveshaftTolerance.components.other.criticalRotationSpeed.components.geometricCharacteristics.inputs.outerRadius.helpButtonText"
            )}
            disabled={isDisabled}
          />
          <HelpInput
            label={`r, ${t("units.length.mm")}`}
            type="number"
            variant="outlined"
            fullWidth
            value={innerRadius || ""} // Bind value to state
            onChange={handleInnerRadiusChange} // Use callback for handling input change
            helpButtonText={t(
              "pages.driveshaftTolerance.components.other.criticalRotationSpeed.components.geometricCharacteristics.inputs.innerRadius.helpButtonText"
            )}
            disabled={isDisabled}
          />
        </InputStack>
        <HelpInput
          label={<div>L, {t("units.length.mm")}</div>}
          type="number"
          variant="outlined"
          sx={{ my: 2 }}
          fullWidth
          value={length || ""} // Bind value to state
          onChange={handleLengthChange} // Use callback for handling input change
          helpButtonText={t(
            "pages.driveshaftTolerance.components.other.criticalRotationSpeed.components.geometricCharacteristics.inputs.length.helpButtonText"
          )}
          disabled={isDisabled}
        />
        <Typography variant="h6" sx={{ my: 2 }}>
          {t("pages.driveshaftTolerance.components.other.criticalRotationSpeed.components.rotationSpeed.title")}
        </Typography>
        <HelpInput
          label={<div>v, {t("units.rotationSpeed.rpm")}</div>}
          type="number"
          variant="outlined"
          fullWidth
          value={rotationSpeed || ""} // Bind value to state
          onChange={handleRotationSpeedChange} // Use callback for handling input change
          helpButtonText={t(
            "pages.driveshaftTolerance.components.other.criticalRotationSpeed.components.rotationSpeed.helpButtonText"
          )}
          disabled={isDisabled}
        />
        <Typography variant="h6" sx={{ my: 2 }}>
          {t("shared.disbalance.result")}
        </Typography>
        <Box
          sx={{
            my: 2,
            p: 2,
            borderWidth: isInvalid ? "1px" : "3px",
            borderColor: indicatorColor,
            borderStyle: "solid",
            borderRadius: "4px",
          }}
        >
          <Typography variant="h6" sx={{ my: 2 }}>
            {t("pages.driveshaftTolerance.components.other.criticalRotationSpeed.components.results.title")}
          </Typography>
          <Typography variant="h6" sx={{ my: 2, color: indicatorColor }}>
            {criticalRotationSpeedText}
          </Typography>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
